import React, { ReactNode, createContext, useContext, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';

import { AcquireTokenGateway } from './AcquireTokenGateway';
import { TabCoordinator } from './tabCoordination';

const AuthGatewayContext = createContext<AcquireTokenGateway | null>(null);

let activeGateway: AcquireTokenGateway | null = null;

export function getActiveGateway(): AcquireTokenGateway | null {
  return activeGateway;
}

interface Props {
  children: ReactNode;
}

export function AuthGatewayProvider({ children }: Props) {
  const { instance } = useMsal();
  const gatewayRef = useRef<AcquireTokenGateway | null>(null);

  if (!gatewayRef.current) {
    gatewayRef.current = new AcquireTokenGateway({
      msal: instance,
      tabCoord: new TabCoordinator(),
    });
    activeGateway = gatewayRef.current;
  }

  useEffect(() => {
    return () => {
      if (gatewayRef.current) {
        gatewayRef.current.dispose();
        if (activeGateway === gatewayRef.current) {
          activeGateway = null;
        }
        gatewayRef.current = null;
      }
    };
  }, []);

  return (
    <AuthGatewayContext.Provider value={gatewayRef.current}>
      {children}
    </AuthGatewayContext.Provider>
  );
}

function useAuthGateway(): AcquireTokenGateway {
  const gateway = useContext(AuthGatewayContext);
  if (!gateway) {
    throw new Error('useAuthGateway used outside AuthGatewayProvider');
  }
  return gateway;
}

function useAuthGatewayOptional(): AcquireTokenGateway | null {
  return useContext(AuthGatewayContext);
}
